import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X, User } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { cents } from "../lib/api";
import { analyticsConsent, setAnalyticsConsent } from "../lib/analytics";
import { InstallNudge } from "./InstallNudge";
import { ScrollToTop, BackToTop } from "./BackToTop";

export function ConsentBanner() {
  const [choice, setChoice] = useState(analyticsConsent());
  if (choice) return null;
  return (
    <div data-testid="analytics-consent-banner" className="fixed bottom-0 inset-x-0 z-[60] bg-[#171715] text-[#FBF6F1] px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex flex-col sm:flex-row items-center justify-center gap-3 text-xs">
      <span>We use privacy-light, cookieless usage analytics (no personal data) to improve the shop. OK?</span>
      <span className="flex gap-2">
        <button data-testid="consent-accept-button" className="bg-[#FBF6F1] text-[#171715] px-5 min-h-[44px] font-semibold" onClick={() => { setAnalyticsConsent("granted"); setChoice("granted"); }}>Allow</button>
        <button data-testid="consent-decline-button" className="border border-[#FBF6F1]/50 px-5 min-h-[44px]" onClick={() => { setAnalyticsConsent("denied"); setChoice("denied"); }}>Decline</button>
      </span>
    </div>
  );
}

const NAV = [
  { to: "/shop", label: "Shop", id: "shop" },
  { to: "/build-your-stack", label: "Build Your Stack", id: "builder" },
  { to: "/subscriptions", label: "Subscribe", id: "subscribe" },
  { to: "/pickup", label: "Pickup Locations", id: "locations" },
  { to: "/journal", label: "Journal", id: "journal" },
  { to: "/our-story", label: "Our Story", id: "story" },
];

export function TestModeBanner() {
  return (
    <div data-testid="test-mode-banner" className="bg-[#A35847] text-[#FBF6F1] text-center text-xs px-3 py-2">
      Test environment — Stripe test mode only. Prices are posted reference prices pending owner confirmation. No live payments are taken.
    </div>
  );
}

export function CartDrawer() {
  const { items, count, bundle, split, nameMap, setQty, drawerOpen, setDrawerOpen } = useCart();
  const navigate = useNavigate();
  if (!drawerOpen) return null;
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)} />
      <aside data-testid="cart-drawer" className="absolute right-0 top-0 h-full w-full max-w-sm bg-[#FBF6F1] border-l border-[#EBD9D0] p-6 overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl">Your Stack</h3>
          <button data-testid="cart-drawer-close-button" aria-label="Close cart" className="min-h-[44px] min-w-[44px] flex items-center justify-center" onClick={() => setDrawerOpen(false)}><X size={20} /></button>
        </div>
        {count === 0 ? (
          <p data-testid="cart-empty-state" className="text-sm text-[#171715]/70">Your stack is empty. Add as many bottles as you like — pack pricing applies automatically.</p>
        ) : (
          <>
            <ul className="space-y-3">
              {Object.entries(items).map(([slug, qty]) => (
                <li key={slug} data-testid={`cart-item-${slug}`} className="card-paper p-3 flex items-center justify-between gap-2">
                  <span className="text-sm font-medium">{nameMap[slug] || slug.replace(/-/g, " ")}</span>
                  <span className="flex items-center gap-1">
                    <button data-testid={`cart-dec-${slug}`} aria-label={`Remove one ${slug}`} className="min-h-[44px] min-w-[44px] border border-[#EBD9D0]" onClick={() => setQty(slug, qty - 1)}>−</button>
                    <span className="w-8 text-center font-mono text-sm">{qty}</span>
                    <button data-testid={`cart-inc-${slug}`} aria-label={`Add one ${slug}`} disabled={count >= 24} className="min-h-[44px] min-w-[44px] border border-[#EBD9D0] disabled:opacity-30" onClick={() => setQty(slug, qty + 1)}>+</button>
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-6 border-t border-[#EBD9D0] pt-4 text-sm">
              <p className="flex justify-between"><span>Bottles</span><span className="font-mono">{count}</span></p>
              {bundle && (
                <p className="flex justify-between font-semibold mt-1"><span>{bundle.label}</span><span data-testid="cart-total" className="font-mono">{cents(bundle.cents)}</span></p>
              )}
              {split && split.traditional > 0 && (
                <p data-testid="cart-split-note" className="text-xs text-[#171715]/65 mt-1 flex justify-between">
                  <span>Functional ({split.functional}) {cents(split.functionalCents)}</span>
                  <span>Traditional ({split.traditional}) {cents(split.traditionalCents)}</span>
                </p>
              )}
              <p className="text-xs text-[#171715]/55 mt-1">3-pack &amp; 6-pack savings apply to functional juices automatically. Traditional juices are $10 each — pack pricing pending confirmation.</p>
            </div>
            <button data-testid="cart-drawer-checkout-button" className="btn-ink w-full mt-6" disabled={!bundle}
              onClick={() => { setDrawerOpen(false); navigate("/cart"); }}>
              Review &amp; choose pickup
            </button>
          </>
        )}
      </aside>
    </div>
  );
}

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const { count, setDrawerOpen } = useCart();
  const { user } = useAuth();
  return (
    <div className="min-h-screen flex flex-col">
      <TestModeBanner />
      <header className="sticky top-0 z-40 bg-[#FBF6F1]/95 backdrop-blur-xl border-b border-[#EBD9D0]">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between h-16">
          <Link to="/" data-testid="nav-brand-logo" aria-label="Tiff. Wellness Co. — home" className="flex items-center py-2">
            <picture>
              <source srcSet="/images/tiff-wordmark.webp" type="image/webp" />
              <img src="/images/tiff-wordmark.jpg" alt="tiff. wellness co." width="112" height="56" className="h-10 w-auto object-contain" />
            </picture>
          </Link>
          <nav className="hidden md:flex items-center gap-5" aria-label="Main">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-link-${n.id}`}
                className={({ isActive }) => `text-sm py-3 ${isActive ? "font-semibold text-[#A35847]" : "text-[#171715]/80 hover:text-[#171715]"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-1">
            <Link to={user && user !== false ? (user.role === "admin" ? "/admin" : user.role === "partner" ? "/partner" : "/account") : "/auth"}
              data-testid="nav-link-account" aria-label="Account"
              className="min-h-[44px] min-w-[44px] flex items-center justify-center"><User size={19} /></Link>
            <button data-testid="nav-cart-trigger-button" aria-label={`Cart, ${count} bottles`} onClick={() => setDrawerOpen(true)}
              className="relative min-h-[44px] min-w-[44px] flex items-center justify-center">
              <ShoppingBag size={19} />
              {count > 0 && <span data-testid="nav-cart-count" className="absolute top-1 right-0 bg-[#A35847] text-[#FBF6F1] text-[10px] rounded-full px-1.5 py-0.5 font-mono">{count}</span>}
            </button>
            <button data-testid="nav-mobile-menu-button" aria-label="Menu" className="md:hidden min-h-[44px] min-w-[44px] flex items-center justify-center" onClick={() => setOpen(!open)}>
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
        {open && (
          <nav data-testid="nav-mobile-menu" className="md:hidden border-t border-[#EBD9D0] bg-[#FBF6F1] px-4 py-2" aria-label="Mobile">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-mobile-link-${n.id}`} onClick={() => setOpen(false)}
                className="block py-3 text-base border-b border-[#EBD9D0]/60 last:border-0">{n.label}</NavLink>
            ))}
          </nav>
        )}
      </header>
      <main className="flex-1">{children}</main>      <CartDrawer />
      <ConsentBanner />
      <InstallNudge />
      <ScrollToTop />
      <BackToTop />
      <footer className="bg-[#171715] text-[#FBF6F1] mt-20">
        <div className="max-w-6xl mx-auto px-4 py-12 grid grid-cols-2 md:grid-cols-4 gap-8 text-sm">
          <div className="col-span-2 md:col-span-1">
            <p className="font-display text-lg mb-2">tiff. wellness co.</p>
            <p className="text-[#FBF6F1]/60 text-xs leading-relaxed">Cold-pressed juice made fresh to order in Dallas, TX. Founded by Tiffany Cotto.</p>
          </div>
          <div>
            <p className="eyebrow !text-[#FBF6F1]/50 mb-3">Learn</p>
            <ul className="space-y-2">
              <li><Link data-testid="footer-link-faq" to="/faq" className="hover:underline">FAQ</Link></li>
              <li><Link data-testid="footer-link-ingredients" to="/ingredients" className="hover:underline">Ingredients &amp; Add-ins</Link></li>
              <li><Link data-testid="footer-link-journal" to="/journal" className="hover:underline">Journal</Link></li>
              <li><Link data-testid="footer-link-contact" to="/contact" className="hover:underline">Contact</Link></li>
            </ul>
          </div>
          <div>
            <p className="eyebrow !text-[#FBF6F1]/50 mb-3">Partner</p>
            <ul className="space-y-2">
              <li><Link data-testid="footer-link-gym-partners" to="/gym-partners" className="hover:underline">Gym Partnerships</Link></li>
              <li><Link data-testid="footer-link-pickup" to="/pickup" className="hover:underline">Pickup Locations</Link></li>
            </ul>
          </div>
          <div>
            <p className="eyebrow !text-[#FBF6F1]/50 mb-3">Policies</p>
            <ul className="space-y-2">
              <li><Link data-testid="footer-link-refund-policy" to="/refund-policy" className="hover:underline">Refund Policy</Link></li>
              <li><Link data-testid="footer-link-subscription-policy" to="/subscription-policy" className="hover:underline">Subscription Policy</Link></li>
              <li><Link data-testid="footer-link-privacy" to="/privacy" className="hover:underline">Privacy</Link></li>
              <li><Link data-testid="footer-link-terms" to="/terms" className="hover:underline">Terms</Link></li>
              <li><Link data-testid="footer-link-accessibility" to="/accessibility" className="hover:underline">Accessibility</Link></li>
              <li><Link data-testid="footer-link-email-preferences" to="/email-preferences" className="hover:underline">Email Preferences</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-6xl mx-auto px-4 py-4 flex flex-col sm:flex-row gap-2 items-center justify-between text-xs text-[#FBF6F1]/50">
            <p>© {new Date().getFullYear()} tiff. wellness co. — Dallas, TX</p>
            <a data-testid="footer-link-instagram" href="https://www.instagram.com/tiff.wellnessco/" target="_blank" rel="noreferrer" className="hover:underline">Instagram @tiff.wellnessco</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
